import { LIFECYCLE_LABEL } from "@/features/animals/lifecycleStates";
import { formatMoney } from "@/features/cost-allocations/utils/formatMoney";
import { Link } from "react-router-dom";
import type { AccountDepreciationItem } from "../api/depreciationApi";

type Props = {
  items: AccountDepreciationItem[];
};

export function DepreciationList({ items }: Props) {
  return (
    <>
      {/* Mobile: cards apiladas */}
      <ul className="space-y-2 md:hidden">
        {items.map((item) => (
          <li key={item.animalId}>
            <Link
              to={`/app/hato/${item.animalId}/timeline`}
              className="flex flex-col gap-2 rounded-2xl border border-app-border bg-app-surface px-4 py-3 transition-colors hover:bg-app-surface-2"
              aria-label={`Ver timeline de ${item.tag}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium font-mono text-[14px] text-app-ink-1">
                    {item.tag}
                  </span>
                  <StateBadge state={item.lifecycleState} isTerminated={item.isTerminated} />
                </div>
                <span className="text-[11px] text-app-ink-3 tabular-nums">
                  Primer parto {formatDate(item.firstCalvingAt)}
                </span>
              </div>
              <dl className="grid grid-cols-3 gap-2 text-[12px]">
                <CardField label="Mes" value={item.monthDepreciation} />
                <CardField label="Acumulado" value={item.accumulatedDepreciation} />
                <CardField label="Valor libros" value={item.remainingBookValue} />
              </dl>
            </Link>
          </li>
        ))}
      </ul>

      {/* Desktop: tabla */}
      <div className="hidden overflow-hidden rounded-2xl border border-app-border bg-app-surface md:block">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-app-border border-b">
              <Th>Tag</Th>
              <Th>Estado</Th>
              <Th>Primer parto</Th>
              <Th align="right">Depreciación del mes</Th>
              <Th align="right">Acumulada</Th>
              <Th align="right">Valor en libros</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-app-border">
            {items.map((item) => (
              <tr
                key={item.animalId}
                className="cursor-pointer transition-colors hover:bg-app-surface-2"
              >
                <Td>
                  <Link
                    to={`/app/hato/${item.animalId}/timeline`}
                    className="block font-medium font-mono text-app-ink-1 hover:text-app-emerald"
                  >
                    {item.tag}
                  </Link>
                </Td>
                <Td>
                  <Link to={`/app/hato/${item.animalId}/timeline`} className="block">
                    <StateBadge state={item.lifecycleState} isTerminated={item.isTerminated} />
                  </Link>
                </Td>
                <Td className="text-app-ink-2 tabular-nums">
                  <Link to={`/app/hato/${item.animalId}/timeline`} className="block">
                    {formatDate(item.firstCalvingAt)}
                  </Link>
                </Td>
                <Td align="right" className="tabular-nums">
                  <Link to={`/app/hato/${item.animalId}/timeline`} className="block">
                    {formatMoney(item.monthDepreciation)}
                  </Link>
                </Td>
                <Td align="right" className="tabular-nums">
                  <Link to={`/app/hato/${item.animalId}/timeline`} className="block">
                    {formatMoney(item.accumulatedDepreciation)}
                  </Link>
                </Td>
                <Td align="right" className="font-medium tabular-nums">
                  <Link to={`/app/hato/${item.animalId}/timeline`} className="block">
                    {formatMoney(item.remainingBookValue)}
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      className={`px-4 py-3 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em] text-${align}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
  align = "left",
}: {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right";
}) {
  return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>;
}

function CardField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] text-app-ink-3 uppercase tracking-[0.05em]">{label}</dt>
      <dd className="font-medium text-app-ink-1 tabular-nums">{formatMoney(value)}</dd>
    </div>
  );
}

function StateBadge({
  state,
  isTerminated,
}: {
  state: AccountDepreciationItem["lifecycleState"];
  isTerminated: boolean;
}) {
  const classes = isTerminated
    ? "bg-neutral-100 text-neutral-500"
    : "bg-app-emerald-bg text-app-emerald";
  return (
    <span className={`inline-block rounded-xl px-2 py-0.5 font-medium text-[11px] ${classes}`}>
      {LIFECYCLE_LABEL[state]}
    </span>
  );
}

function formatDate(iso: string): string {
  // YYYY-MM-DD → DD/MM/YYYY (es-CR-friendly without locale Intl drift).
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
